import type { AppState, Field, Product } from '../types';
import { getSession } from './cloud';
import {
  generateDxf,
  generateExtendedDxf,
  generateSurveyorCsv,
  generatePrj,
  type DxfShape,
  type ExtendedDxfOptions,
} from './dxf';

export function isMobileEnvironment(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  if (
    typeof window !== 'undefined' &&
    window.innerWidth <= 768 &&
    (navigator.maxTouchPoints || 0) > 0
  ) {
    return true;
  }
  return false;
}

export async function download(blob: Blob, filename: string): Promise<void> {
  // On mobile browsers where Web Share API with files is available, use native OS share (Files, AirDrop, rover apps)
  if (
    isMobileEnvironment() &&
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function'
  ) {
    try {
      const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
      if (typeof navigator.canShare !== 'function' || navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: filename,
        });
        return;
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') return;
      // Fall through to link download if native share fails
    }
  }

  // Anchor download: ensure link is attached to document.body (required for mobile WebKit / Safari iOS)
  if (typeof document !== 'undefined') {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export function downloadState(state: AppState, prefix = 'plotwise') {
  void download(
    new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' }),
    `${prefix}-${new Date().toISOString().slice(0, 10)}.json`,
  );
}

export function downloadDxf(fields: (Field | DxfShape)[], estateName = 'revir') {
  const safe = estateName.replace(/[^a-zæøå0-9]/gi, '-').toLowerCase() || 'revir';
  const content = generateDxf(fields);
  void download(new Blob([content], { type: 'application/dxf' }), `plotwise-${safe}.dxf`);
}

export function downloadSurveyorCsv(fields: (Field | DxfShape)[], estateName = 'revir') {
  const safe = estateName.replace(/[^a-zæøå0-9]/gi, '-').toLowerCase() || 'revir';
  const content = generateSurveyorCsv(fields);
  void download(
    new Blob([content], { type: 'text/csv;charset=utf-8' }),
    `plotwise-${safe}-punktliste.csv`,
  );
}

export interface GisExportClientOptions extends ExtendedDxfOptions {
  crs?: 'EPSG:25832' | 'EPSG:4326';
  downloadPrjSidecar?: boolean;
  token?: string;
}

export async function downloadGeoPackage(
  estateName = 'revir',
  fields: (Field | DxfShape)[] = [],
  options?: GisExportClientOptions,
): Promise<void> {
  const safe =
    estateName
      .toLowerCase()
      .replace(/[^a-z0-9æøå_-]/gi, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'revir';
  const filename = `plotwise-${safe}.gpkg`;

  const session = await getSession().catch(() => null);
  const token =
    options?.token ||
    session?.access_token ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('plotwise.auth.token') : '') ||
    '';
  const res = await fetch('/api/professional/gis-export', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      estateName,
      fields,
      options,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Serverfejl ${res.status} ved generering af GeoPackage.`);
  }

  const blob = await res.blob();
  await download(blob, filename);
}

export async function downloadExtendedDxf(
  estateName = 'revir',
  fields: (Field | DxfShape)[] = [],
  options?: GisExportClientOptions,
): Promise<void> {
  const safe =
    estateName
      .toLowerCase()
      .replace(/[^a-z0-9æøå_-]/gi, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'revir';
  const dxfContent = generateExtendedDxf(fields, options);
  await download(new Blob([dxfContent], { type: 'application/dxf' }), `plotwise-${safe}.dxf`);

  if (options?.downloadPrjSidecar) {
    const prjContent = generatePrj();
    await download(new Blob([prjContent], { type: 'text/plain' }), `plotwise-${safe}.prj`);
  }
}

export interface ExportOrderLine {
  product: Product;
  areaHa: number;
  needKg: number;
  packs: number;
}

// Spreadsheet text must never be interpreted as a formula, even in a custom catalog.
function cell(value: string | number) {
  const text = String(value);
  const safe = /^[\s]*[=+@-]/.test(text) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

export function orderCsv(lines: ExportOrderLine[]): string {
  return (
    '\uFEFF' +
    [
      [
        'Produkt',
        'Areal ha',
        'Behov',
        'Enhed',
        'Pakker',
        'Mængde pr. pakke',
        'Pris pr. pakke DKK',
        'Subtotal DKK',
      ],
      ...lines.map(({ product, areaHa, needKg, packs }) => [
        product.name,
        areaHa,
        needKg,
        product.unit ?? 'kg',
        packs,
        product.packKg,
        product.priceDkk,
        packs * product.priceDkk,
      ]),
    ]
      .map((row) => row.map(cell).join(';'))
      .join('\r\n')
  );
}

export function downloadOrderCsv(lines: ExportOrderLine[]) {
  void download(
    new Blob([orderCsv(lines)], { type: 'text/csv;charset=utf-8' }),
    'plotwise-indkoebsliste.csv',
  );
}
